import type { BlendedSearchResult } from '@mrb91002/shared-types'
import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { searchPatterns } from '../../api/fetchers/search'

import styles from './SearchBar.module.css'

interface SearchBarProps {
  className?: string
  placeholder?: string
}

export const SearchBar: React.FC<SearchBarProps> = ({
  className = '',
  placeholder = 'Search patterns and users...',
}) => {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [results, setResults] = useState<BlendedSearchResult[]>([])
  const [error, setError] = useState<string | null>(null)

  const searchRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setResults([])
      setIsOpen(false)
      return
    }

    const timeoutId = setTimeout(async () => {
      setIsLoading(true)
      setError(null)

      try {
        const searchResponse = await searchPatterns(query, 8)
        setResults(searchResponse.items || [])
        setIsOpen(true)
      } catch (err: unknown) {
        setError('Search failed')
        setResults([])
      } finally {
        setIsLoading(false)
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [query])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`)
      setIsOpen(false)
      inputRef.current?.blur()
    }
  }

  const handlePatternClick = (patternId: string) => {
    navigate(`/pattern/${patternId}`)
    setIsOpen(false)
    setQuery('')
  }

  const handleUserClick = (screenName: string) => {
    navigate(`/author/${screenName}`)
    setIsOpen(false)
    setQuery('')
  }

  const handleViewAllResults = () => {
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`)
      setIsOpen(false)
    }
  }

  const handleAdvancedSearch = () => {
    navigate('/search?advanced=true&type=patterns')
    setIsOpen(false)
  }

  return (
    <div className={`${styles.searchContainer} ${className}`} ref={searchRef}>
      <form onSubmit={handleSubmit} className={styles.searchForm}>
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className={styles.searchInput}
          autoComplete="off"
        />
        <button type="submit" className={styles.searchButton}>
          <svg className={styles.searchIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </button>
        <button type="button" onClick={handleAdvancedSearch} className={styles.advancedButton} title="Advanced Search">
          <svg className={styles.filterIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
        </button>
      </form>

      {isOpen && (
        <div className={styles.dropdown}>
          {isLoading && (
            <div className={styles.loading}>
              <div className={styles.spinner} />
              Searching...
            </div>
          )}

          {error && <div className={styles.error}>{error}</div>}

          {!isLoading && !error && results.length > 0 && (
            <>
              {results.map((result) =>
                result.type === 'pattern' && result.pattern ? (
                  <button
                    key={`pattern-${result.pattern.pattern_id}`}
                    className={styles.resultItem}
                    onClick={() => handlePatternClick(result.pattern!.pattern_id)}
                  >
                    <div className={styles.resultContent}>
                      <div className={styles.resultTitle}>{result.pattern.title}</div>
                      <div className={styles.resultMeta}>
                        {result.pattern.difficulty} • {result.pattern.tags.slice(0, 2).join(', ')}
                      </div>
                    </div>
                    {result.pattern.images[0] && (
                      <img src={result.pattern.images[0]} alt={result.pattern.title} className={styles.resultImage} />
                    )}
                  </button>
                ) : result.type === 'user' && result.user ? (
                  <button
                    key={`user-${result.user.screen_name}`}
                    className={styles.resultItem}
                    onClick={() => handleUserClick(result.user!.screen_name)}
                  >
                    <div className={styles.resultContent}>
                      <div className={styles.resultTitle}>{result.user.screen_name}</div>
                      <div className={styles.resultMeta}>Reputation: {result.user.reputation_score}</div>
                    </div>
                    <img src={result.user.photo} alt={result.user.screen_name} className={styles.userAvatar} />
                  </button>
                ) : null,
              )}

              <button className={styles.viewAllButton} onClick={handleViewAllResults}>
                View all results for "{query}"
              </button>
            </>
          )}

          {!isLoading && !error && results.length === 0 && query.length >= 2 && (
            <div className={styles.noResults}>No results found for "{query}"</div>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchBar
